import React, { useState, useEffect } from 'react'
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, FormGroup, Label, Input, Alert, Spinner,
} from 'reactstrap'
import api from '../../../services/api'

const AddRuleModal = ({ companyId, isOpen, toggle, onCreated }) => {
  const [observedDomains, setObservedDomains] = useState([])
  const [commTypes, setCommTypes] = useState([])
  const [users, setUsers] = useState([])
  const [ruleType, setRuleType] = useState('Domain')
  const [upnDomain, setUpnDomain] = useState('')
  const [aliasDomain, setAliasDomain] = useState('')
  const [commTypeId, setCommTypeId] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const [domainsRes, typesRes, usersRes] = await Promise.all([
          api.get(`/company/${companyId}/email-sync/observed-domains`),
          api.get(`/company/${companyId}/email-sync/comm-types`),
          api.get(`/company/${companyId}/email-sync/users`),
        ])
        if (cancelled) return
        if (domainsRes.ok) setObservedDomains(await domainsRes.json())
        if (typesRes.ok) setCommTypes(await typesRes.json())
        if (usersRes.ok) setUsers(await usersRes.json())
      } catch {
        if (!cancelled) setError('Failed to load form data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [companyId])

  const upnDomainOptions = Array.from(new Set(users.map(u => u.upnDomain).filter(Boolean))).sort()
  const aliasDomainOptions = observedDomains.filter(d =>
    !upnDomain || d.toLowerCase() !== upnDomain.toLowerCase())

  const handleSave = async () => {
    if (ruleType === 'Domain' && (!upnDomain || !aliasDomain || !commTypeId)) {
      setError('All fields required for a domain rule')
      return
    }
    if (ruleType === 'PrimarySmtpMismatch' && !commTypeId) {
      setError('Preferred slot is required')
      return
    }

    try {
      setSaving(true)
      setError(null)

      const body = {
        ruleType,
        preferredCommTypeId: parseInt(commTypeId, 10),
        upnDomain: ruleType === 'Domain' ? upnDomain : null,
        aliasDomain: ruleType === 'Domain' ? aliasDomain : null,
      }

      const res = await api.post(`/company/${companyId}/email-sync/rules`, body)
      if (res.ok) {
        onCreated()
      } else {
        const text = await res.text()
        setError(text || 'Failed to create rule')
      }
    } catch {
      setError('Failed to create rule')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Add Email Sync Rule</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center p-3"><Spinner color="primary" /></div>
        ) : (
          <>
            <FormGroup>
              <Label for="ruleType">Rule type</Label>
              <Input
                type="select"
                id="ruleType"
                value={ruleType}
                onChange={(e) => setRuleType(e.target.value)}
              >
                <option value="Domain">Domain pair (UPN domain → alias domain)</option>
                <option value="PrimarySmtpMismatch">Primary SMTP mismatch (any user whose primary SMTP differs from UPN)</option>
              </Input>
              <small className="text-muted">
                {ruleType === 'Domain'
                  ? 'For users on a specific UPN domain, push their primary SMTP on the chosen alias domain into the slot.'
                  : 'For ANY user whose primary SMTP doesn\'t match their UPN, push the primary SMTP into the slot. At most one of these per company.'}
              </small>
            </FormGroup>

            {ruleType === 'Domain' && (
              <>
                <FormGroup>
                  <Label for="upnDomain">UPN domain (matches users by)</Label>
                  <Input
                    type="select"
                    id="upnDomain"
                    value={upnDomain}
                    onChange={(e) => setUpnDomain(e.target.value)}
                  >
                    <option value="">-- Select --</option>
                    {upnDomainOptions.map(d => <option key={d} value={d}>{d}</option>)}
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label for="aliasDomain">Alias domain (source of address to push)</Label>
                  <Input
                    type="select"
                    id="aliasDomain"
                    value={aliasDomain}
                    onChange={(e) => setAliasDomain(e.target.value)}
                    disabled={!upnDomain}
                  >
                    <option value="">-- Select --</option>
                    {aliasDomainOptions.map(d => <option key={d} value={d}>{d}</option>)}
                  </Input>
                  {observedDomains.length === 0 && (
                    <small className="text-muted">
                      No proxy domains observed yet. Run a sync to populate.
                    </small>
                  )}
                </FormGroup>
              </>
            )}

            <FormGroup>
              <Label for="commTypeId">Preferred CW slot</Label>
              <Input
                type="select"
                id="commTypeId"
                value={commTypeId}
                onChange={(e) => setCommTypeId(e.target.value)}
              >
                <option value="">-- Select --</option>
                {commTypes.map(t => (
                  <option key={t.id} value={t.id}>{t.description} (id {t.id})</option>
                ))}
              </Input>
              <small className="text-muted">
                If this slot is occupied at sync time, sync falls back to the other email slot.
              </small>
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>Cancel</Button>
        <Button color="primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? <Spinner size="sm" /> : 'Create rule'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default AddRuleModal
